import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Container,
  Items,
  LeftScrollButton,
  RightScrollButton,
} from "./styles/ProjectSection";
import Project from "./Project";
import profile from "./config/profile";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";

const { projects } = profile;

const Projects = () => {
  const trackRef = useRef(null);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(false);

  // Read the real scroll position, so the arrows stay right however the cards were scrolled
  const updateArrows = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    setCanScrollBack(track.scrollLeft > 1);
    setCanScrollForward(
      track.scrollLeft + track.clientWidth < track.scrollWidth - 1,
    );
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    updateArrows();
    const resizeObserver = new ResizeObserver(updateArrows);
    resizeObserver.observe(track);

    return () => resizeObserver.disconnect();
  }, [updateArrows]);

  // Page by whole cards so none is skipped: forward brings the first card that isn't fully
  // visible to the start, back brings the nearest hidden card on the left fully into view
  const scrollByPage = (direction) => {
    const track = trackRef.current;
    const inset = parseFloat(getComputedStyle(track).scrollPaddingLeft) || 0;
    const viewStart = track.scrollLeft + inset;
    const viewEnd = track.scrollLeft + track.clientWidth - inset;
    const cards = [...track.children];
    let target;

    if (direction > 0) {
      // Skip only the card already at the start, in case a card is wider than the view (phones)
      const next = cards.find(
        (card) =>
          card.offsetLeft > viewStart + 1 &&
          card.offsetLeft + card.offsetWidth > viewEnd + 1,
      );
      target = next ? next.offsetLeft - inset : track.scrollWidth;
    } else {
      const previous = [...cards]
        .reverse()
        .find((card) => card.offsetLeft < viewStart - 1);
      target = previous
        ? Math.min(
            previous.offsetLeft - inset,
            previous.offsetLeft +
              previous.offsetWidth -
              (viewEnd - viewStart) -
              inset,
          )
        : 0;
    }

    track.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
  };

  return (
    <Container>
      <LeftScrollButton
        aria-label="Previous projects"
        onClick={() => scrollByPage(-1)}
        $visible={canScrollBack}
      >
        <LeftOutlined />
      </LeftScrollButton>
      <Items ref={trackRef} onScroll={updateArrows}>
        {projects.map((props) => (
          <Project key={props.id} {...props} />
        ))}
      </Items>
      <RightScrollButton
        aria-label="Next projects"
        onClick={() => scrollByPage(1)}
        $visible={canScrollForward}
      >
        <RightOutlined />
      </RightScrollButton>
    </Container>
  );
};

export default Projects;
